import React from 'react';
import { Link } from 'react-router-dom';
interface ButtonProps {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'gradient-primary' | 'gradient-purple';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  onClick?: () => void;
  href?: string;
  external?: boolean;
}
export const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  className = '',
  onClick,
  href,
  external
}: ButtonProps) => {
  const baseStyles = 'font-semibold transition-all duration-300 rounded-lg inline-flex items-center justify-center backdrop-blur-sm';
  const sizes = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg'
  };
  const variants = {
    'gradient-primary': 'bg-gradient-to-r from-[#6805DA] to-[#4B0599] text-white hover:from-[#7916eb] hover:to-[#5a06b8] shadow-lg shadow-[#6805DA]/10 hover:shadow-xl hover:shadow-[#6805DA]/20 hover:scale-[1.02] active:scale-[0.98] border border-transparent',
    'gradient-purple': 'bg-gradient-to-r from-[#6805DA] to-[#4B0599] text-white hover:from-[#7916eb] hover:to-[#5a06b8] shadow-lg shadow-[#6805DA]/10 hover:shadow-xl hover:shadow-[#6805DA]/20 hover:scale-[1.02] active:scale-[0.98] border border-transparent',
    primary: 'bg-gradient-to-r from-[#6805DA] to-[#4B0599] text-white hover:from-[#7916eb] hover:to-[#5a06b8] shadow-lg shadow-[#6805DA]/10 hover:shadow-xl hover:shadow-[#6805DA]/20 hover:scale-[1.02] active:scale-[0.98] border border-transparent',
    secondary: 'bg-gradient-to-r from-white/10 to-white/5 border border-white/20 text-white hover:from-white/15 hover:to-white/10 hover:border-white/30 hover:scale-[1.02] active:scale-[0.98] shadow-sm hover:shadow',
    outline: 'border border-white/20 text-white hover:bg-white/5 backdrop-blur-sm hover:border-white/40 hover:scale-[1.02] active:scale-[0.98]',
    ghost: 'text-white/80 hover:text-white hover:bg-white/5 hover:scale-[1.02] active:scale-[0.98]'
  };
  const buttonClass = `${baseStyles} ${sizes[size]} ${variants[variant]} ${className}`;
  // Handle anchor links with # for smooth scrolling
  const handleAnchorClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const targetId = href!.substring(1);
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  // If there's an onClick handler, render a button
  if (onClick) {
    return <button className={buttonClass} onClick={onClick} data-id="element-350">
        {children}
      </button>;
  }
  // If there's no href, render a button
  if (!href) {
    return <button className={buttonClass} data-id="element-351">{children}</button>;
  }
  // For external links or links that start with http
  if (external || href.startsWith('http')) {
    return <a href={href} className={buttonClass} target="_blank" rel="noopener noreferrer" data-id="element-352">
        {children}
      </a>;
  }
  // For anchor links that start with #
  if (href.startsWith('#')) {
    return <a href={href} className={buttonClass} onClick={handleAnchorClick} data-id="element-353">
        {children}
      </a>;
  }
  // For internal navigation routes (like /start)
  return <Link to={href} className={buttonClass} data-id="element-354">
      {children}
    </Link>;
};